import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { expenseApprovalService } from '../services/api/expenseApprovals';
import { useAuthStore } from '../store/authstore';
import { SubmitApprovalDecisionRequest } from '../types/expenses';

const APPROVAL_KEY = 'expenseApprovals';

export const usePendingApprovals = (params?: { page?: number; limit?: number }) => {
  const { session } = useAuthStore();
  const instituteId = session?.user?.instituteId ?? 0;
  return useQuery({
    queryKey: [APPROVAL_KEY, instituteId, 'pending', params],
    queryFn: () => expenseApprovalService.getPendingApprovals(params),
    enabled: instituteId > 0,
    staleTime: 60 * 1000,
  });
};

export const usePendingApprovalsCount = () => {
  const { session } = useAuthStore();
  const instituteId = session?.user?.instituteId ?? 0;
  return useQuery({
    queryKey: [APPROVAL_KEY, instituteId, 'pending', 'count'],
    queryFn: async () => {
      const result = await expenseApprovalService.getPendingApprovals({ page: 1, limit: 1 });
      return result.totalCount ?? 0;
    },
    enabled: instituteId > 0,
    staleTime: 2 * 60 * 1000,
  });
};

export const useApprovalTimeline = (claimId: number | null) => {
  const { session } = useAuthStore();
  const instituteId = session?.user?.instituteId ?? 0;
  return useQuery({
    queryKey: [APPROVAL_KEY, instituteId, 'timeline', claimId],
    queryFn: () => expenseApprovalService.getTimeline(claimId!),
    enabled: !!claimId && instituteId > 0,
    staleTime: 30 * 1000,
  });
};

export const useSubmitApprovalDecision = () => {
  const { session } = useAuthStore();
  const instituteId = session?.user?.instituteId ?? 0;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitApprovalDecisionRequest) =>
      expenseApprovalService.submitDecision(data),
    onSuccess: () => {
      toast.success('Decision submitted successfully');
      queryClient.invalidateQueries({ queryKey: [APPROVAL_KEY, instituteId] });
      queryClient.invalidateQueries({ queryKey: ['expenseClaims', instituteId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to submit decision');
    },
  });
};
